import React, { useState, useEffect } from 'react';
import { 
  ArrowLeft, 
  BarChart3, 
  TrendingUp, 
  Users, 
  BookOpen, 
  Award, 
  Calendar,
  Download,
  Filter,
  RefreshCw
} from 'lucide-react';
import { useModules } from '../contexts/ModuleContext';
import { useUsers } from '../contexts/UserContext';

const TrainingAnalytics = ({ onBack }) => {
  const { modules } = useModules();
  const { users, getUserStats } = useUsers();
  const [selectedPeriod, setSelectedPeriod] = useState('30d');
  const [selectedModule, setSelectedModule] = useState('all');
  const [selectedDepartment, setSelectedDepartment] = useState('all');
  const [analyticsData, setAnalyticsData] = useState({});

  useEffect(() => {
    generateAnalyticsData();
  }, [selectedPeriod, selectedModule, selectedDepartment]);

  const generateAnalyticsData = () => {
    // Simulate analytics data generation
    const totalUsers = users.length;
    const totalModules = modules.length;
    const completedModules = Math.floor(totalUsers * 0.72); // 72% completion rate
    const averageScore = 87;
    
    // Generate department-wise data
    const departmentStats = users.reduce((acc, user) => {
      if (!acc[user.department]) {
        acc[user.department] = {
          totalUsers: 0,
          completedModules: 0,
          averageScore: 0,
          modules: []
        };
      }
      acc[user.department].totalUsers++;
      acc[user.department].completedModules += Math.floor(Math.random() * 3) + 1;
      acc[user.department].averageScore = Math.floor(Math.random() * 20) + 80;
      return acc;
    }, {});

    // Generate module completion data
    const moduleStats = modules.map(module => ({
      id: module.id,
      title: module.title,
      totalAssignments: module.assignments?.users?.length || 0,
      completedBy: Math.floor((module.assignments?.users?.length || 0) * 0.8),
      averageScore: Math.floor(Math.random() * 20) + 80,
      difficulty: module.difficulty
    }));

    // Generate monthly progress data
    const monthlyProgress = [
      { month: 'Jan', completed: 12, assigned: 15 },
      { month: 'Feb', completed: 18, assigned: 22 },
      { month: 'Mar', completed: 25, assigned: 30 },
      { month: 'Apr', completed: 32, assigned: 38 },
      { month: 'May', completed: 28, assigned: 35 },
      { month: 'Jun', completed: 35, assigned: 42 }
    ];

    setAnalyticsData({
      totalUsers,
      totalModules,
      completedModules,
      averageScore,
      departmentStats,
      moduleStats,
      monthlyProgress
    });
  };

  const getCompletionRate = () => {
    if (analyticsData.totalModules === 0) return 0;
    return Math.round((analyticsData.completedModules / (analyticsData.totalUsers * analyticsData.totalModules)) * 100);
  };

  const getTopPerformingDepartment = () => {
    if (!analyticsData.departmentStats) return null;
    return Object.entries(analyticsData.departmentStats)
      .sort(([,a], [,b]) => b.averageScore - a.averageScore)[0];
  };

  const getMostPopularModule = () => {
    if (!analyticsData.moduleStats) return null;
    return analyticsData.moduleStats
      .sort((a, b) => b.totalAssignments - a.totalAssignments)[0];
  };

  const exportAnalytics = () => {
    const csvContent = [
      ['Metric', 'Value'],
      ['Total Users', analyticsData.totalUsers],
      ['Total Modules', analyticsData.totalModules],
      ['Completed Modules', analyticsData.completedModules],
      ['Average Score', analyticsData.averageScore + '%'],
      ['Completion Rate', getCompletionRate() + '%'],
      ['', ''],
      ['Department', 'Users', 'Completed', 'Avg Score'],
      ...Object.entries(analyticsData.departmentStats || {}).map(([dept, stats]) => [
        dept, stats.totalUsers, stats.completedModules, stats.averageScore + '%'
      ])
    ].map(row => row.join(',')).join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'training-analytics.csv';
    a.click();
    window.URL.revokeObjectURL(url);
  };

  return (
    <div className="container">
      <div className="analytics-header">
        <button onClick={onBack} className="btn btn-secondary back-to-dashboard">
          <ArrowLeft size={16} style={{ marginRight: '8px' }} />
          Back to Dashboard
        </button>
        <div>
          <h1 className="text-3xl font-bold">Training Analytics</h1>
          <p className="text-lg text-gray-600">View detailed reports and analytics on training progress</p>
        </div>
        <div className="analytics-controls">
          <button onClick={generateAnalyticsData} className="btn btn-secondary">
            <RefreshCw size={16} style={{ marginRight: '8px' }} />
            Refresh
          </button>
          <button onClick={exportAnalytics} className="btn btn-primary">
            <Download size={16} style={{ marginRight: '8px' }} />
            Export
          </button>
        </div>
      </div>

      <div className="analytics-filters">
        <div className="filter-group">
          <label>Time Period</label>
          <select
            value={selectedPeriod}
            onChange={(e) => setSelectedPeriod(e.target.value)}
            className="filter-select"
          >
            <option value="7d">Last 7 days</option>
            <option value="30d">Last 30 days</option>
            <option value="90d">Last 90 days</option>
            <option value="1y">Last year</option>
          </select>
        </div>
        
        <div className="filter-group">
          <label>Module</label>
          <select
            value={selectedModule}
            onChange={(e) => setSelectedModule(e.target.value)}
            className="filter-select"
          >
            <option value="all">All Modules</option>
            {modules.map(module => (
              <option key={module.id} value={module.id}>{module.title}</option>
            ))}
          </select>
        </div>
        
        <div className="filter-group">
          <label>Department</label>
          <select
            value={selectedDepartment}
            onChange={(e) => setSelectedDepartment(e.target.value)}
            className="filter-select"
          >
            <option value="all">All Departments</option>
            {Object.keys(analyticsData.departmentStats || {}).map(dept => (
              <option key={dept} value={dept}>{dept}</option>
            ))}
          </select>
        </div>
      </div>

      <div className="analytics-overview">
        <div className="grid grid-4">
          <div className="card stat-card">
            <div className="stat-icon">
              <Users size={24} color="#3b82f6" />
            </div>
            <div className="stat-content">
              <h3 className="text-2xl font-bold">{analyticsData.totalUsers}</h3>
              <p className="text-gray-600">Total Users</p>
            </div>
          </div>
          
          <div className="card stat-card">
            <div className="stat-icon">
              <BookOpen size={24} color="#059669" />
            </div>
            <div className="stat-content">
              <h3 className="text-2xl font-bold">{analyticsData.completedModules}</h3>
              <p className="text-gray-600">Completed Modules</p>
            </div>
          </div>
          
          <div className="card stat-card">
            <div className="stat-icon">
              <Award size={24} color="#f59e0b" />
            </div>
            <div className="stat-content">
              <h3 className="text-2xl font-bold">{analyticsData.averageScore}%</h3>
              <p className="text-gray-600">Average Score</p>
            </div>
          </div>
          
          <div className="card stat-card">
            <div className="stat-icon">
              <TrendingUp size={24} color="#8b5cf6" />
            </div>
            <div className="stat-content">
              <h3 className="text-2xl font-bold">{getCompletionRate()}%</h3>
              <p className="text-gray-600">Completion Rate</p>
            </div>
          </div>
        </div>
      </div>

      <div className="analytics-charts">
        <div className="grid grid-2">
          <div className="card">
            <div className="card-header">
              <h3 className="text-xl font-bold">Department Performance</h3>
              <p className="text-gray-600">Average scores by department</p>
            </div>
            <div className="chart-container">
              {Object.entries(analyticsData.departmentStats || {}).map(([dept, stats]) => (
                <div key={dept} className="chart-item">
                  <div className="chart-label">
                    <span className="dept-name">{dept}</span>
                    <span className="dept-score">{stats.averageScore}%</span>
                  </div>
                  <div className="progress-bar">
                    <div 
                      className="progress-fill" 
                      style={{ width: `${stats.averageScore}%` }}
                    ></div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="card">
            <div className="card-header">
              <h3 className="text-xl font-bold">Module Completion</h3>
              <p className="text-gray-600">Completion rates by module</p>
            </div>
            <div className="chart-container">
              {analyticsData.moduleStats?.slice(0, 5).map((module) => {
                const completionRate = module.totalAssignments > 0 
                  ? Math.round((module.completedBy / module.totalAssignments) * 100)
                  : 0;
                return (
                  <div key={module.id} className="chart-item">
                    <div className="chart-label">
                      <span className="module-name">{module.title}</span>
                      <span className="module-rate">{completionRate}%</span>
                    </div>
                    <div className="progress-bar">
                      <div 
                        className="progress-fill" 
                        style={{ width: `${completionRate}%` }}
                      ></div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      <div className="analytics-tables">
        <div className="grid grid-2">
          <div className="card">
            <div className="card-header">
              <h3 className="text-xl font-bold">Top Performing Departments</h3>
            </div>
            <div className="table-container">
              <table className="analytics-table">
                <thead>
                  <tr>
                    <th>Department</th>
                    <th>Users</th>
                    <th>Completed</th>
                    <th>Avg Score</th>
                  </tr>
                </thead>
                <tbody>
                  {Object.entries(analyticsData.departmentStats || {})
                    .sort(([,a], [,b]) => b.averageScore - a.averageScore)
                    .slice(0, 5)
                    .map(([dept, stats]) => (
                      <tr key={dept}>
                        <td>{dept}</td>
                        <td>{stats.totalUsers}</td>
                        <td>{stats.completedModules}</td>
                        <td>
                          <span className="score-badge">{stats.averageScore}%</span>
                        </td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>
          </div>

          <div className="card">
            <div className="card-header">
              <h3 className="text-xl font-bold">Most Popular Modules</h3>
            </div>
            <div className="table-container">
              <table className="analytics-table">
                <thead>
                  <tr>
                    <th>Module</th>
                    <th>Assigned</th>
                    <th>Completed</th>
                    <th>Rate</th>
                  </tr>
                </thead>
                <tbody>
                  {analyticsData.moduleStats?.slice(0, 5).map((module) => {
                    const completionRate = module.totalAssignments > 0 
                      ? Math.round((module.completedBy / module.totalAssignments) * 100)
                      : 0;
                    return (
                      <tr key={module.id}>
                        <td>{module.title}</td>
                        <td>{module.totalAssignments}</td>
                        <td>{module.completedBy}</td>
                        <td>
                          <span className="rate-badge">{completionRate}%</span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

      <div className="analytics-insights">
        <div className="card">
          <div className="card-header">
            <h3 className="text-xl font-bold">Key Insights</h3>
          </div>
          <div className="insights-grid">
            <div className="insight-item">
              <div className="insight-icon">
                <TrendingUp size={24} color="#059669" />
              </div>
              <div className="insight-content">
                <h4 className="insight-title">Top Department</h4>
                <p className="insight-text">
                  {getTopPerformingDepartment()?.[0]} is leading with {getTopPerformingDepartment()?.[1]?.averageScore}% average score
                </p>
              </div>
            </div>
            
            <div className="insight-item">
              <div className="insight-icon">
                <BookOpen size={24} color="#3b82f6" />
              </div>
              <div className="insight-content">
                <h4 className="insight-title">Most Popular Module</h4>
                <p className="insight-text">
                  {getMostPopularModule()?.title} has {getMostPopularModule()?.totalAssignments} assignments
                </p>
              </div>
            </div>
            
            <div className="insight-item">
              <div className="insight-icon">
                <Award size={24} color="#f59e0b" />
              </div>
              <div className="insight-content">
                <h4 className="insight-title">Overall Performance</h4>
                <p className="insight-text">
                  {getCompletionRate()}% completion rate with {analyticsData.averageScore}% average score
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TrainingAnalytics;
